import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { MessageCircle, X, Send, User, Bot, Loader2, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { getSessionId, getVisitorId, trackEvent } from "@/utils/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [chatId, setChatId] = useState(null);
  const [chatStatus, setChatStatus] = useState("bot");
  const [isLoading, setIsLoading] = useState(false);
  const [showOptions, setShowOptions] = useState([]);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  
  // Proactive message state
  const [proactiveMessage, setProactiveMessage] = useState(null);
  const [showNotificationBubble, setShowNotificationBubble] = useState(false);
  const [triggeredEvents, setTriggeredEvents] = useState(new Set());
  
  // Automation settings
  const [automationSettings, setAutomationSettings] = useState({
    chatbot_auto_open: false,
    chatbot_auto_open_delay: 30,
    chatbot_auto_message_enabled: true,
    chatbot_auto_message_delay: 15,
    chatbot_auto_message_text: "👋 Need help planning your celebration? I'm here to assist!",
    chatbot_scroll_trigger_enabled: true,
    chatbot_scroll_trigger_percent: 50,
    chatbot_scroll_trigger_message: "I see you're exploring our venue! Would you like a quick estimate?",
    chatbot_exit_intent_enabled: true,
    chatbot_exit_intent_message: "Wait! Before you go, would you like to save your estimate or speak with our team?",
    chatbot_idle_trigger_enabled: false,
    chatbot_idle_trigger_seconds: 60,
    chatbot_idle_trigger_message: "Still there? Let me know if you have any questions!"
  });
  
  // Chatbot settings
  const [chatSettings, setChatSettings] = useState({
    chatbot_name: "KAI Assistant",
    chatbot_subtitle: "We typically reply instantly",
    chatbot_avatar_url: "",
    chatbot_primary_color: "#B08968",
    chatbot_phone_number: "+91 98765 43210"
  });

  // Refs for timers and tracking
  const delayTimerRef = useRef(null);
  const idleTimerRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const hasInteractedRef = useRef(false);

  // Parse boolean/number from string settings
  const parseSettingBool = (val) => val === "true" || val === true;
  const parseSettingNum = (val) => parseInt(val, 10) || 0;

  // Fetch chatbot settings on mount
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/chat/settings`);
        const data = response.data;
        
        // Update basic chat settings
        setChatSettings(prev => ({
          ...prev,
          chatbot_name: data.chatbot_name || prev.chatbot_name,
          chatbot_subtitle: data.chatbot_subtitle || prev.chatbot_subtitle,
          chatbot_avatar_url: data.chatbot_avatar_url || prev.chatbot_avatar_url,
          chatbot_primary_color: data.chatbot_primary_color || prev.chatbot_primary_color,
          chatbot_phone_number: data.chatbot_phone_number || prev.chatbot_phone_number
        }));
        
        // Update automation settings
        setAutomationSettings({
          chatbot_auto_open: parseSettingBool(data.chatbot_auto_open),
          chatbot_auto_open_delay: parseSettingNum(data.chatbot_auto_open_delay),
          chatbot_auto_message_enabled: parseSettingBool(data.chatbot_auto_message_enabled),
          chatbot_auto_message_delay: parseSettingNum(data.chatbot_auto_message_delay),
          chatbot_auto_message_text: data.chatbot_auto_message_text || "👋 Need help planning your celebration?",
          chatbot_scroll_trigger_enabled: parseSettingBool(data.chatbot_scroll_trigger_enabled),
          chatbot_scroll_trigger_percent: parseSettingNum(data.chatbot_scroll_trigger_percent),
          chatbot_scroll_trigger_message: data.chatbot_scroll_trigger_message || "I see you're exploring our venue!",
          chatbot_exit_intent_enabled: parseSettingBool(data.chatbot_exit_intent_enabled),
          chatbot_exit_intent_message: data.chatbot_exit_intent_message || "Wait! Before you go...",
          chatbot_idle_trigger_enabled: parseSettingBool(data.chatbot_idle_trigger_enabled),
          chatbot_idle_trigger_seconds: parseSettingNum(data.chatbot_idle_trigger_seconds),
          chatbot_idle_trigger_message: data.chatbot_idle_trigger_message || "Still there?"
        });
      } catch (error) {
        console.error("Failed to fetch chat settings:", error);
      }
    };
    fetchSettings();
  }, []);

  // Show proactive message in notification bubble
  const showProactiveNotification = useCallback((message, triggerType) => {
    if (isOpen || hasInteractedRef.current || triggeredEvents.has(triggerType)) return;
    
    setTriggeredEvents(prev => new Set([...prev, triggerType]));
    setProactiveMessage(message);
    setShowNotificationBubble(true);
    trackEvent("chat_proactive_shown", { trigger: triggerType });
    
    // Auto-hide after 10 seconds if not interacted
    setTimeout(() => {
      setShowNotificationBubble(false);
    }, 10000);
  }, [isOpen, triggeredEvents]);

  // Delay-based proactive message
  useEffect(() => {
    if (!automationSettings.chatbot_auto_message_enabled) return;
    
    const delay = automationSettings.chatbot_auto_message_delay * 1000;
    
    delayTimerRef.current = setTimeout(() => {
      showProactiveNotification(automationSettings.chatbot_auto_message_text, "delay");
    }, delay);
    
    return () => {
      if (delayTimerRef.current) clearTimeout(delayTimerRef.current);
    };
  }, [automationSettings.chatbot_auto_message_enabled, automationSettings.chatbot_auto_message_delay, automationSettings.chatbot_auto_message_text, showProactiveNotification]);

  // Scroll-based trigger
  useEffect(() => {
    if (!automationSettings.chatbot_scroll_trigger_enabled) return;
    
    const handleScroll = () => {
      const scrollPercent = (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100;
      
      if (scrollPercent >= automationSettings.chatbot_scroll_trigger_percent) {
        showProactiveNotification(automationSettings.chatbot_scroll_trigger_message, "scroll");
      }
    };
    
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [automationSettings.chatbot_scroll_trigger_enabled, automationSettings.chatbot_scroll_trigger_percent, automationSettings.chatbot_scroll_trigger_message, showProactiveNotification]);

  // Exit intent trigger
  useEffect(() => {
    if (!automationSettings.chatbot_exit_intent_enabled) return;
    
    const handleMouseLeave = (e) => {
      if (e.clientY <= 0) {
        showProactiveNotification(automationSettings.chatbot_exit_intent_message, "exit_intent");
      }
    };
    
    document.addEventListener("mouseleave", handleMouseLeave);
    return () => document.removeEventListener("mouseleave", handleMouseLeave);
  }, [automationSettings.chatbot_exit_intent_enabled, automationSettings.chatbot_exit_intent_message, showProactiveNotification]);

  // Idle trigger
  useEffect(() => {
    if (!automationSettings.chatbot_idle_trigger_enabled) return;
    
    const resetIdleTimer = () => {
      lastActivityRef.current = Date.now();
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      
      idleTimerRef.current = setTimeout(() => {
        showProactiveNotification(automationSettings.chatbot_idle_trigger_message, "idle");
      }, automationSettings.chatbot_idle_trigger_seconds * 1000);
    };
    
    const events = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    events.forEach(event => window.addEventListener(event, resetIdleTimer, { passive: true }));
    resetIdleTimer();
    
    return () => {
      events.forEach(event => window.removeEventListener(event, resetIdleTimer));
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [automationSettings.chatbot_idle_trigger_enabled, automationSettings.chatbot_idle_trigger_seconds, automationSettings.chatbot_idle_trigger_message, showProactiveNotification]);

  // Auto-scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Start chat when widget opens
  const handleOpenChat = async () => {
    setIsOpen(true);
    setIsMinimized(false);
    setShowNotificationBubble(false);
    hasInteractedRef.current = true;
    trackEvent("chat_widget_opened");

    if (messages.length === 0) {
      setIsLoading(true);
      try {
        const response = await axios.post(`${API}/chat/start`, null, {
          params: {
            session_id: getSessionId(),
            visitor_id: getVisitorId()
          }
        });

        setChatId(response.data.chat_id);
        
        if (response.data.initial_message) {
          setMessages([{
            id: response.data.initial_message.id,
            sender: "bot",
            message: response.data.initial_message.message,
            options: response.data.initial_message.metadata?.options
          }]);
          setShowOptions(response.data.initial_message.metadata?.options || []);
        }
        
        if (response.data.existing && response.data.chat) {
          // Load existing messages
          const chatResponse = await axios.get(`${API}/chat/${response.data.chat_id}/messages`);
          if (chatResponse.data.messages) {
            setMessages(chatResponse.data.messages.map(m => ({
              id: m.id,
              sender: m.sender,
              message: m.message,
              options: m.metadata?.options
            })));
          }
          setChatStatus(response.data.chat.status);
        }
      } catch (error) {
        console.error("Failed to start chat:", error);
        setMessages([{
          id: "error",
          sender: "bot",
          message: "Sorry, I'm having trouble connecting. Please try again or call us directly."
        }]);
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Send message
  const sendMessage = async (text) => {
    if (!text.trim()) return;

    const userMessage = {
      id: `user_${Date.now()}`,
      sender: "user",
      message: text
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue("");
    setShowOptions([]);
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/chat/message`, {
        session_id: getSessionId(),
        message: text,
        visitor_id: getVisitorId()
      });

      if (response.data.bot_response) {
        const botMsg = {
          id: response.data.bot_response.id,
          sender: "bot",
          message: response.data.bot_response.message,
          options: response.data.bot_response.metadata?.options
        };
        setMessages(prev => [...prev, botMsg]);
        setShowOptions(response.data.bot_response.metadata?.options || []);
      }

      if (response.data.chat_status) {
        setChatStatus(response.data.chat_status);
      }

      if (response.data.handover_requested) {
        trackEvent("chat_handover_requested");
      }
    } catch (error) {
      console.error("Failed to send message:", error);
      setMessages(prev => [...prev, {
        id: `error_${Date.now()}`,
        sender: "bot",
        message: "Sorry, I couldn't process that. Please try again."
      }]);
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  // Handle option click
  const handleOptionClick = (option) => {
    sendMessage(option);
  };

  // Handle form submit
  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(inputValue);
  };

  // Close widget
  const handleClose = () => {
    setIsOpen(false);
    trackEvent("chat_widget_closed");
  };

  // Minimize widget
  const handleMinimize = () => {
    setIsMinimized(true);
  };

  // Handle clicking the proactive message bubble
  const handleProactiveBubbleClick = () => {
    setShowNotificationBubble(false);
    handleOpenChat();
  };

  // Closed state - show chat button with optional notification bubble
  if (!isOpen) {
    return (
      <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-2">
        {/* Proactive message bubble */}
        {showNotificationBubble && proactiveMessage && (
          <div 
            onClick={handleProactiveBubbleClick}
            className="max-w-[280px] bg-white rounded-2xl shadow-xl p-4 cursor-pointer animate-in slide-in-from-right-5 fade-in duration-300 border border-gray-100"
            data-testid="proactive-message-bubble"
          >
            <div className="flex items-start gap-3">
              <div 
                className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: chatSettings.chatbot_primary_color }}
              >
                {chatSettings.chatbot_avatar_url ? (
                  <img 
                    src={chatSettings.chatbot_avatar_url} 
                    alt={chatSettings.chatbot_name}
                    className="w-full h-full rounded-full object-cover"
                  />
                ) : (
                  <Bot className="w-4 h-4 text-white" />
                )}
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-800">{chatSettings.chatbot_name}</p>
                <p className="text-sm text-gray-600 mt-1">{proactiveMessage}</p>
              </div>
            </div>
            <button 
              onClick={(e) => { e.stopPropagation(); setShowNotificationBubble(false); }}
              className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
        
        {/* Main chat button */}
        <button
          onClick={handleOpenChat}
          className="w-14 h-14 text-white rounded-full shadow-lg flex items-center justify-center transition-all duration-300 hover:scale-110"
          style={{ backgroundColor: chatSettings.chatbot_primary_color }}
          data-testid="chat-widget-button"
          aria-label="Open chat"
        >
          {chatSettings.chatbot_avatar_url ? (
            <img 
              src={chatSettings.chatbot_avatar_url} 
              alt={chatSettings.chatbot_name}
              className="w-10 h-10 rounded-full object-cover"
            />
          ) : (
            <MessageCircle className="w-6 h-6" />
          )}
          {/* Notification dot */}
          {showNotificationBubble && (
            <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full animate-pulse" />
          )}
          {!showNotificationBubble && (
            <span className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full animate-pulse" />
          )}
        </button>
      </div>
    );
  }

  // Minimized state
  if (isMinimized) {
    return (
      <button
        onClick={() => setIsMinimized(false)}
        className="fixed bottom-6 right-6 z-50 text-white px-4 py-3 rounded-full shadow-lg flex items-center gap-2 transition-all duration-300"
        style={{ backgroundColor: chatSettings.chatbot_primary_color }}
      >
        {chatSettings.chatbot_avatar_url ? (
          <img 
            src={chatSettings.chatbot_avatar_url} 
            alt={chatSettings.chatbot_name}
            className="w-6 h-6 rounded-full object-cover"
          />
        ) : (
          <MessageCircle className="w-5 h-5" />
        )}
        <span className="text-sm font-medium">Continue Chat</span>
        <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
      </button>
    );
  }

  // Full chat widget
  return (
    <div 
      className="fixed bottom-6 right-6 z-50 w-[380px] max-w-[calc(100vw-48px)] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col"
      style={{ height: "min(600px, calc(100vh - 120px))" }}
      data-testid="chat-widget"
    >
      {/* Header */}
      <div 
        className="text-white p-4 flex items-center justify-between"
        style={{ background: `linear-gradient(to right, ${chatSettings.chatbot_primary_color}, ${chatSettings.chatbot_primary_color}dd)` }}
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center overflow-hidden">
            {chatSettings.chatbot_avatar_url ? (
              <img 
                src={chatSettings.chatbot_avatar_url} 
                alt={chatSettings.chatbot_name}
                className="w-full h-full object-cover"
              />
            ) : chatStatus === "human" ? (
              <User className="w-5 h-5" />
            ) : (
              <Bot className="w-5 h-5" />
            )}
          </div>
          <div>
            <h3 className="font-semibold text-sm">{chatSettings.chatbot_name}</h3>
            <p className="text-xs text-white/80">
              {chatStatus === "human" ? "Speaking with our team" : 
               chatStatus === "waiting_handover" ? "Connecting you..." : 
               chatSettings.chatbot_subtitle}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button 
            onClick={handleMinimize}
            className="p-2 hover:bg-white/10 rounded-full transition-colors"
            aria-label="Minimize"
          >
            <div className="w-4 h-0.5 bg-white" />
          </button>
          <button 
            onClick={handleClose}
            className="p-2 hover:bg-white/10 rounded-full transition-colors"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`flex ${msg.sender === "user" ? "justify-end" : "justify-start"}`}
          >
            <div
              className={`max-w-[85%] rounded-2xl px-4 py-2.5 ${
                msg.sender === "user"
                  ? "bg-[#B08968] text-white rounded-br-md"
                  : msg.sender === "agent"
                  ? "bg-blue-500 text-white rounded-bl-md"
                  : "bg-white text-gray-800 shadow-sm rounded-bl-md"
              }`}
              style={msg.sender === "user" ? { backgroundColor: chatSettings.chatbot_primary_color } : {}}
            >
              <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
            </div>
          </div>
        ))}

        {/* Quick Options */}
        {showOptions.length > 0 && !isLoading && (
          <div className="flex flex-wrap gap-2 mt-2">
            {showOptions.map((option, index) => (
              <button
                key={index}
                onClick={() => handleOptionClick(option)}
                className="px-4 py-2 text-sm bg-white border rounded-full transition-colors"
                style={{ 
                  borderColor: chatSettings.chatbot_primary_color,
                  color: chatSettings.chatbot_primary_color
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = chatSettings.chatbot_primary_color;
                  e.target.style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'white';
                  e.target.style.color = chatSettings.chatbot_primary_color;
                }}
              >
                {option}
              </button>
            ))}
          </div>
        )}

        {/* Loading indicator */}
        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-white rounded-2xl px-4 py-3 shadow-sm rounded-bl-md">
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Status bar for handover */}
      {chatStatus === "waiting_handover" && (
        <div className="px-4 py-2 bg-amber-50 border-t border-amber-200">
          <p className="text-xs text-amber-700 flex items-center gap-2">
            <Loader2 className="w-3 h-3 animate-spin" />
            Connecting you with our team...
          </p>
        </div>
      )}

      {/* Input */}
      <form onSubmit={handleSubmit} className="p-4 border-t bg-white">
        <div className="flex items-center gap-2">
          <Input
            ref={inputRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder={
              chatStatus === "waiting_handover" 
                ? "Waiting for agent..." 
                : "Type your message..."
            }
            className="flex-1 rounded-full border-gray-200"
            style={{ 
              "--tw-ring-color": chatSettings.chatbot_primary_color,
              borderColor: inputValue ? chatSettings.chatbot_primary_color : undefined
            }}
            disabled={isLoading}
            data-testid="chat-input"
          />
          <Button
            type="submit"
            size="icon"
            className="rounded-full w-10 h-10"
            style={{ backgroundColor: chatSettings.chatbot_primary_color }}
            disabled={isLoading || !inputValue.trim()}
            data-testid="chat-send"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
        
        {/* Quick call CTA */}
        {chatSettings.chatbot_phone_number && (
          <div className="mt-2 text-center">
            <a 
              href={`tel:${chatSettings.chatbot_phone_number.replace(/\s/g, '')}`}
              className="text-xs text-gray-500 flex items-center justify-center gap-1 hover:text-gray-700"
            >
              <Phone className="w-3 h-3" />
              Prefer to call? {chatSettings.chatbot_phone_number}
            </a>
          </div>
        )}
      </form>
    </div>
  );
};

export default ChatWidget;
